/**
 * The filter model (1.N-2 deliverable 15). Pure: which operators a column takes, how they read,
 * how a typed value becomes the JSON the server wants, and how a filter reads as a chip. The
 * server stays the arbiter (1.U deliverable 2): these rules only keep obvious mistakes in the
 * dialog and out of the URL.
 */
import {
  CANON_STATUSES,
  CATALOGUE_KIND_OPERATORS,
  type CatalogueOperator,
  DISCOVERY_STATES,
  PLAY_STATES,
} from '@codex/contracts';
import type { ColumnDef } from './columns.js';
import type { Filter } from './view.js';

export const MAX_IN_VALUES = 100;
export const MAX_TEXT_VALUE = 200;
export const MISSING_PAGE = '(missing page)';

/** The operators a column takes: the contract's table, never a local copy. */
export function operatorsFor(column: ColumnDef): readonly CatalogueOperator[] {
  return CATALOGUE_KIND_OPERATORS[column.kind];
}

const DATE_KINDS = new Set(['real_date', 'world_date', 'updatedAt']);
const OPTION_KINDS = new Set(['select', 'multi_select']);

/** How an operator reads on a column of this kind ("is at least", "is on or after"). */
export function operatorLabel(kind: ColumnDef['kind'], op: CatalogueOperator): string {
  const dates = DATE_KINDS.has(kind);
  const options = OPTION_KINDS.has(kind);
  switch (op) {
    case 'eq':
      return 'is';
    case 'neq':
      return 'is not';
    case 'lt':
      return dates ? 'is before' : options ? 'comes before' : 'is less than';
    case 'lte':
      return dates ? 'is on or before' : options ? 'comes at or before' : 'is at most';
    case 'gt':
      return dates ? 'is after' : options ? 'comes after' : 'is more than';
    case 'gte':
      return dates ? 'is on or after' : options ? 'comes at or after' : 'is at least';
    case 'contains':
      return 'contains';
    case 'in':
      return 'is any of';
    case 'empty':
      return 'is empty';
    case 'notEmpty':
      return 'is not empty';
  }
}

/** The value editor a column gets (1.B-2 deliverable 4's families). */
export type Family = 'text' | 'number' | 'boolean' | 'choice' | 'date' | 'reference' | 'image';

export function familyOf(kind: ColumnDef['kind']): Family {
  switch (kind) {
    case 'number':
      return 'number';
    case 'boolean':
      return 'boolean';
    case 'select':
    case 'multi_select':
    case 'canonStatus':
    case 'playState':
    case 'discovery':
      return 'choice';
    case 'real_date':
    case 'world_date':
    case 'updatedAt':
      return 'date';
    case 'doc_ref':
    case 'doc_refs':
      return 'reference';
    case 'asset':
      return 'image';
    default:
      return 'text';
  }
}

/** Every value a link may carry on a choice column: the full contract enums. */
function acceptedOptions(column: ColumnDef): readonly string[] {
  switch (column.kind) {
    case 'canonStatus':
      return CANON_STATUSES;
    case 'playState':
      return PLAY_STATES;
    default:
      return optionsOf(column);
  }
}

/** The options the dialog offers on a choice column, in order. */
export function optionsOf(column: ColumnDef): readonly string[] {
  switch (column.kind) {
    case 'canonStatus':
      return CANON_STATUSES.filter((state) => state !== 'reference');
    case 'playState':
      return PLAY_STATES.filter((state) => state !== 'n_a');
    case 'discovery':
      return DISCOVERY_STATES;
    default:
      return column.options ?? [];
  }
}

/** The columns the dialog offers: every column, in-world dates included (stage s3). */
export function filterableColumns(columns: readonly ColumnDef[]): ColumnDef[] {
  return [...columns];
}

/**
 * Whether the dialog can edit this operator on this column. An `in` on a free-text, number or
 * date kind can only arrive in a link: it is kept and shown, and the dialog offers removal.
 */
export function canEdit(column: ColumnDef, op: CatalogueOperator): boolean {
  if (op !== 'in') return true;
  const family = familyOf(column.kind);
  return family === 'choice' || family === 'reference';
}

type Atom = 'string' | 'number' | 'date' | 'boolean' | 'option' | 'uuid' | 'iso';
type Shape = { readonly list: boolean; readonly atom: Atom } | null;

/** What value an operator carries on a column; null for `empty` and `notEmpty`. */
function shapeOf(column: ColumnDef, op: CatalogueOperator): Shape {
  if (op === 'empty' || op === 'notEmpty') return null;
  if (op === 'contains') return { list: false, atom: 'string' };
  const list = op === 'in';
  const comparison = op === 'lt' || op === 'lte' || op === 'gt' || op === 'gte';
  const family = familyOf(column.kind);
  let atom: Atom;
  if (column.kind === 'updatedAt') atom = 'iso';
  else if (column.kind === 'dice') atom = comparison ? 'number' : 'string';
  else if (family === 'number') atom = 'number';
  else if (family === 'boolean') atom = 'boolean';
  else if (family === 'choice') atom = 'option';
  else if (family === 'date') atom = column.kind === 'world_date' ? 'string' : 'date';
  else if (family === 'reference') atom = 'uuid';
  else atom = 'string';
  return { list, atom };
}

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NUMBER = /^[+-]?(\d+\.?\d*|\.\d+)(e[+-]?\d+)?$/i;

function validDate(text: string): boolean {
  if (!DATE.test(text)) return false;
  const [y, m, d] = text.split('-').map(Number) as [number, number, number];
  const at = new Date(Date.UTC(y, m - 1, d));
  return at.getUTCFullYear() === y && at.getUTCMonth() === m - 1 && at.getUTCDate() === d;
}

/** Whether an already-JSON value has the type its operator takes (used for links). */
function atomFits(column: ColumnDef, atom: Atom, value: unknown): boolean {
  switch (atom) {
    case 'string':
      return typeof value === 'string' && value.length > 0;
    case 'number':
      return typeof value === 'number' && Number.isFinite(value);
    case 'boolean':
      return typeof value === 'boolean';
    case 'date':
      return typeof value === 'string' && validDate(value);
    case 'uuid':
      return typeof value === 'string' && UUID.test(value);
    case 'iso':
      return typeof value === 'string' && !Number.isNaN(Date.parse(value));
    case 'option': {
      if (typeof value !== 'string') return false;
      const options = acceptedOptions(column);
      return options.length === 0 || options.includes(value);
    }
  }
}

/** Whether a filter's value suits its column and operator (JSON type only). */
export function valueFits(column: ColumnDef, filter: Filter): boolean {
  const shape = shapeOf(column, filter.op);
  if (shape === null) return filter.value === undefined;
  const { value } = filter;
  if (!shape.list) return atomFits(column, shape.atom, value);
  return (
    Array.isArray(value) &&
    value.length >= 1 &&
    value.length <= MAX_IN_VALUES &&
    value.every((item) => atomFits(column, shape.atom, item))
  );
}

type Json = NonNullable<Filter['value']>;

export type ParseResult = { readonly value: Json | undefined } | { readonly error: string };

function parseAtom(column: ColumnDef, atom: Atom, op: CatalogueOperator, raw: string): ParseResult {
  const text = raw.trim();
  switch (atom) {
    case 'number': {
      const n = NUMBER.test(text) ? Number(text) : Number.NaN;
      if (!Number.isFinite(n)) return { error: 'Enter a number.' };
      return { value: n };
    }
    case 'date':
      return validDate(text) ? { value: text } : { error: 'Enter a date as YYYY-MM-DD.' };
    case 'iso': {
      if (!validDate(text)) return { error: 'Enter a date as YYYY-MM-DD.' };
      const [y, m, d] = text.split('-').map(Number) as [number, number, number];
      return { value: new Date(y, m - 1, d).toISOString() };
    }
    case 'boolean':
      return text === 'true' || text === 'false'
        ? { value: text === 'true' }
        : { error: 'Choose Yes or No.' };
    case 'option':
      return optionsOf(column).includes(text) ? { value: text } : { error: 'Choose an option.' };
    case 'uuid':
      return UUID.test(text) ? { value: text } : { error: 'Pick a page.' };
    case 'string':
      if (text === '') return { error: 'Enter a value.' };
      if (op === 'contains' && (text.length > MAX_TEXT_VALUE || /[\r\n]/.test(text)))
        return { error: 'Enter up to 200 characters on one line.' };
      return { value: text };
  }
}

/**
 * The JSON value a typed input stands for, or one sentence saying why not. `input` is a string, or
 * a list of strings for `in`. `empty` and `notEmpty` take none (`value` is undefined).
 */
export function parseValue(
  column: ColumnDef,
  op: CatalogueOperator,
  input: string | readonly string[],
): ParseResult {
  const shape = shapeOf(column, op);
  if (shape === null) return { value: undefined };
  if (!shape.list) {
    if (typeof input !== 'string') return { error: 'Enter a value.' };
    return parseAtom(column, shape.atom, op, input);
  }
  const items = typeof input === 'string' ? [input] : input;
  if (items.length < 1) return { error: 'Choose at least one value.' };
  if (items.length > MAX_IN_VALUES) return { error: 'Choose up to 100 values.' };
  const out: Json[] = [];
  for (const item of items) {
    const parsed = parseAtom(column, shape.atom, op, item);
    if ('error' in parsed) return parsed;
    out.push(parsed.value as Json);
  }
  return { value: out };
}

const DAY = new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' });

function valueText(
  column: ColumnDef,
  value: unknown,
  titles: ReadonlyMap<string, string | null>,
): string {
  if (Array.isArray(value)) return value.map((item) => valueText(column, item, titles)).join(', ');
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return String(value);
  if (typeof value !== 'string') return '';
  if (familyOf(column.kind) === 'reference' && UUID.test(value)) {
    const title = titles.get(value);
    return title === undefined ? '…' : (title ?? MISSING_PAGE);
  }
  if (column.kind === 'updatedAt') {
    const at = new Date(value);
    return Number.isNaN(at.getTime()) ? value : DAY.format(at);
  }
  return value;
}

/**
 * "<label> <operator> <value>"; a doc ref reads by its title (`titles` maps an id to its title, or
 * to null for a trashed or missing page; an id not yet loaded reads "…").
 */
export function chipText(
  filter: Filter,
  column: ColumnDef,
  titles: ReadonlyMap<string, string | null> = new Map(),
): string {
  const head = `${column.label} ${operatorLabel(column.kind, filter.op)}`;
  if (filter.value === undefined) return head;
  const reference = familyOf(column.kind) === 'reference' && filter.op !== 'contains';
  return `${head} ${valueText(reference ? column : { ...column, kind: 'text' }, filter.value, titles)}`;
}

/** The ids a set of filters name on reference columns (for `docs.titles`). */
export function referencedIds(filters: readonly Filter[], columns: readonly ColumnDef[]): string[] {
  const ids = new Set<string>();
  for (const filter of filters) {
    const column = columns.find((c) => c.key === filter.column);
    if (!column || familyOf(column.kind) !== 'reference' || filter.op === 'contains') continue;
    const values = Array.isArray(filter.value) ? filter.value : [filter.value];
    for (const value of values) if (typeof value === 'string' && UUID.test(value)) ids.add(value);
  }
  return [...ids];
}
